import React from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import PropTypes from 'prop-types';
import Utils from '../../../../../utils';
import Chip from '@material-ui/core/Chip';

export const SelectMui4Field = props => {
    const {
        id,
        title,
        options,
        emptyValue,
        error,
        placeholder,
        errorText,
        helperText,
        required,
        disabled,
        multiple,
        inputValue,
        onBlur,
        onFocus,
        valueOptions,
        uiSchema
    } = props;

    const { classes = {} } = uiSchema;
    const onChange = event => {
        const target = event.target;
        const value = target.value;
        const selectOptions = target.options;

        // native selects return string values
        // MUI multiple selects returns array,
        // to handle the same way, extract the selected options
        if (!multiple || (Utils.isArray(value) && !options.native)) {
            return props.onChange(id, value === '' ? emptyValue : value);
        }

        const selectedValues = [];

        for (let i = 0, l = selectOptions.length; i < l; i += 1) {
            if (selectOptions[i].selected) {
                selectedValues.push(selectOptions[i].value);
            }
        }

        return props.onChange(id, selectedValues);
    };

    const handleDelete = valueToDelete => {
        const filteredSelectedValues = inputValue.filter(selectedValue => selectedValue !== valueToDelete);

        return props.onChange(id, filteredSelectedValues);
    };

    // todo: this should work im Mui 4
    // todo: labelWidth does not work on Select on Mui 3
    const inputLabel = React.useRef(null);
    const [labelWidth, setLabelWidth] = React.useState(0);

    React.useEffect(() => {
        setLabelWidth(inputLabel.current.offsetWidth);
    }, []);

    const selectOptions = [];

    if (placeholder !== null) {
        if (options.native) {
            selectOptions.push(<option key={`${id}-option-empty`} value="">{ placeholder }</option>);
        } else {
            selectOptions.push(<MenuItem key={`${id}-option-empty`} value="">{ placeholder }</MenuItem>);
        }
    }

    valueOptions.forEach((valueOption, i) => {
        const { key, label } = valueOption;
        const optionDisabled = disabled ? disabled : valueOption.disabled;

        if (options.native) {
            selectOptions.push(
                <option disabled={ optionDisabled } key={`${id}-option-${i}`} value={ key }>{ label }</option>
            );
        } else {
            const style = {};

            if (valueOption.depth) {
                style.paddingLeft = `${valueOption.depth * options.depthIndentRem}rem`;
            }

            selectOptions.push(
                <MenuItem style={ style } disabled={ optionDisabled } key={`${id}-option-${i}`} value={ key }>{ label }</MenuItem>
            );
        }
    });

    let renderValue = null;

    if (options.showAsChip) {
        const labels = {};

        valueOptions.forEach(option => {
            labels[option.key] = option.label;
        });

        if (multiple) {
            renderValue = selected => (
                <div className={ classes.chipContainer || '' }>
                    {selected.map(value => (<Chip key={value} onMouseDown={event => {
                        event.stopPropagation();
                    }} onDelete={() => handleDelete(value)} label={labels[value]}
                    className={classes.chip || ''}/>))}
                </div>
            );
        } else {
            renderValue = selected => (
                <div className={classes.chipContainer || ''}>
                    <Chip key={selected} label={labels[selected]} className={classes.chip || ''}/>
                </div>
            );
        }
    }

    return (
        <React.Fragment>
            <FormControl
                required={ required }
                error={ error }
                component="fieldset"
                variant={ options.variant || 'outlined' }
                classes={{
                    root: classes.fieldSet || ''
                }}
            >
                <InputLabel
                    ref={ inputLabel }
                    htmlFor={ id }
                >
                    { title }
                </InputLabel>
                <Select
                    id={ id }
                    native={ options.native }
                    label={ title }
                    labelWidth={labelWidth}
                    classes={{
                        root: classes.selectField || ''
                    }}
                    multiple={ multiple }
                    variant={ options.variant ? options.variant : 'outlined' }
                    onChange={ !disabled ? onChange : null }
                    placeholder={ placeholder }
                    error={ error }
                    required={ required }
                    disabled={ disabled }
                    value={ inputValue || '' }
                    onBlur={ onBlur && (event => onBlur({ id, value: event.target.value })) }
                    onFocus={ onFocus && (event => onFocus({ id, value: event.target.value })) }
                    inputProps={{
                        name: title,
                        id,
                        'data-cy': `select-${id}-field`
                    }}
                    renderValue={ renderValue }
                >
                    { selectOptions }
                </Select>
                { helperText ? <FormHelperText
                    classes={{
                        root: classes.helperContainer || ''
                    }}
                >{ helperText }</FormHelperText> : null }
                { errorText ? <FormHelperText
                    classes={{
                        root: classes.errorContainer || ''
                    }}
                >{ errorText }</FormHelperText> : null }
            </FormControl>
        </React.Fragment>
    );
};

SelectMui4Field.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    emptyValue: PropTypes.any,
    placeholder: PropTypes.any,
    error: PropTypes.bool,
    errorText: PropTypes.string,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    multiple: PropTypes.bool,
    options: PropTypes.shape({
        variant: PropTypes.string,
        native: PropTypes.bool,
        showAsChip: PropTypes.bool,
        depthIndentRem: PropTypes.number
    }),
    valueOptions: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        disabled: PropTypes.bool
    })),
    uiSchema: PropTypes.shape({
        classes: PropTypes.shape({
            fieldSet: PropTypes.string,
            helperContainer: PropTypes.string,
            errorContainer: PropTypes.string,
            selectField: PropTypes.string,
            chipContainer: PropTypes.string,
            chip: PropTypes.string
        })
    }),
    inputValue: PropTypes.any,
    onBlur: PropTypes.func,
    onFocus: PropTypes.func,
    helperText: PropTypes.string,
    onChange: PropTypes.func
};

SelectMui4Field.defaultProps = {
    title: '',
    emptyValue: '',
    placeholder: null,
    error: false,
    errorText: '',
    required: false,
    disabled: false,
    multiple: false,
    options: {
        variant: 'outlined',
        native: true,
        showAsChip: false,
        depthIndentRem: 1
    },
    valueOptions: [],
    uiSchema: {},
    inputValue: '',
    helperText: ''
};
